import { Done } from '@mui/icons-material';
import { Alert, Box, Button, Divider, FormControl, FormLabel, Paper, Typography } from '@mui/material';
import type { ICompanyCalcualtions } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import editCalculations from '@src/APIs/graphQl/Company/editCalculations';
import NumberInput from '@src/App/Components/General/FormularElements/formsControls/inputs/NumberInput/NumberInput';
import type Company from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface CalculationsTabProps {
    calculations: ICompanyCalcualtions;
    setCompany: (company: Company) => void;
}
/**
 * Represents a CalculationsTab component.
 *
 * @param {CalculationsTabProps} params - The parameters for the CalculationsTab component.
 * @param {Object} params.calculations - The calculations data.
 * @param {Function} params.setCompany - The function to set the company.
 *
 * @returns {JSX.Element} The JSX element representing the CalculationsTab component.
 */
export function CalculationsTab({ calculations, setCompany }: CalculationsTabProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [calculationsState, setCalculationsState] = useState<ICompanyCalcualtions>(calculations);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [changed, setChanged] = useState<boolean>(false);

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.calculations}
                </Typography>
                <Divider />
                <Typography variant="body2" color="text.secondary">
                    {langStrings.calculationsText}
                </Typography>

                <FormControl>
                    <FormLabel>{langStrings.pastForecastMonth}</FormLabel>
                    <NumberInput
                        min={0}
                        max={12}
                        value={calculationsState.workedMonthCount}
                        onChange={(value) => {
                            setChanged(true);
                            setCalculationsState({ ...calculationsState, workedMonthCount: value });
                        }}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{langStrings.futureForecastMonths}</FormLabel>
                    <NumberInput
                        min={0}
                        max={12}
                        value={calculationsState.forecastMonthCount}
                        onChange={(value) => {
                            setChanged(true);
                            setCalculationsState({ ...calculationsState, forecastMonthCount: value });
                        }}
                    />
                </FormControl>

                {saveError && (
                    <Alert severity="error" onClose={() => setSaveError(null)}>
                        {saveError}
                    </Alert>
                )}

                {changed && (
                    <Button
                        variant="contained"
                        startIcon={<Done />}
                        disabled={saving}
                        onClick={() => {
                            setSaving(true);
                            editCalculations(calculationsState)
                                .then(setCompany)
                                .then(setChanged.bind(null, false))
                                .catch((err) => setSaveError(err.toString()))
                                .finally(() => setSaving(false));
                        }}
                    >
                        {langStrings.save}
                    </Button>
                )}
            </Box>
        </Paper>
    );
}
